/**
 * Which notes still have audio uploading after Stop, in this page (#14423).
 *
 * Stop waits `RECORDING_STOP_DRAIN_GRACE_MS` for the upload queue and then
 * settles the recording anyway. The queue no longer stops there: the bar's
 * capture keeps draining it in the background (`BarCaptureSession.stop`), and
 * the chunk route accepts those chunks as LATE audio. While it drains, the
 * person has to be told — the note they land on would otherwise read as
 * finished without its last seconds. This is the one place that fact lives:
 * the capture REPORTS each recording's remaining chunk count, and the note's
 * processing row and the bar's notes pill READ whether their note has any.
 *
 * Keyed by recording, reported with its note, because the capture knows its
 * recording and the page knows its note. Page memory only: the durable copy
 * of each chunk is the IndexedDB buffer, and a later visit's recovery pass
 * uploads what a closed tab left — this store says only what THIS page is
 * sending now.
 *
 * Client-safe and dependency-free; subscribe with `useSyncExternalStore`.
 */

/** What the note's processing row and the bar's notes pill say meanwhile. */
export const LATE_AUDIO_UPLOADING_LABEL =
  'Uploading the last few seconds of audio…'

/**
 * What they say once the audio is up and the note is being re-derived with
 * it, until the re-run's summary lands (#14423 review) — the server's
 * `lateAudioPending` (`./late-audio-pending`).
 */
export const LATE_AUDIO_UPDATING_LABEL =
  'Updating the note with the last few seconds…'

type Entry = { noteId: string | null; pendingCount: number }

const entries = new Map<string, Entry>()
const listeners = new Set<() => void>()

function uploadingNotes(): Set<string> {
  const notes = new Set<string>()
  for (const entry of entries.values()) {
    if (entry.noteId !== null && entry.pendingCount > 0) notes.add(entry.noteId)
  }
  return notes
}

function sameSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const value of a) if (!b.has(value)) return false
  return true
}

/**
 * Report how many chunks one recording still has to upload after Stop. Zero
 * (or less) forgets it. Subscribers hear only when the set of uploading NOTES
 * changes — a count ticking down is not news to either reader.
 */
export function reportLateAudio(
  recordingId: string,
  noteId: string | null,
  pendingCount: number,
): void {
  const before = uploadingNotes()
  if (pendingCount > 0) entries.set(recordingId, { noteId, pendingCount })
  else entries.delete(recordingId)
  if (sameSet(before, uploadingNotes())) return
  for (const listener of listeners) {
    try {
      listener()
    } catch {
      // A reader's failure is its own; the rest still hear.
    }
  }
}

/** Whether any recording of `noteId` is still uploading after Stop. */
export function isLateAudioUploadingForNote(noteId: string | null): boolean {
  if (noteId === null) return false
  return uploadingNotes().has(noteId)
}

/** `useSyncExternalStore`'s subscribe. */
export function subscribeLateAudio(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
